import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';

import { enderecoDeArquivo } from '@/shared/api/client';
import { policies, type IssuanceReportLink, type Policy } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Button } from '@/shared/ui/Button';
import { Icon } from '@/shared/ui/Icon';
import { tiposDeAcao } from '@/shared/ui/tiposDeAcao';
import { iconTone } from '@/shared/ui/tones';
import { useToast } from '@/shared/ui/useToast';

import {
  avisoDoModeloDoLaudo,
  motivoSemLaudo,
  PERMISSAO_DO_LAUDO,
  rotuloDaAssinatura,
} from './laudo';
import { motivoSemDocumento, temDocumentoDaApolice } from './documentoDaApolice';

/**
 * Menu "Baixar arquivos" da apolice, com os itens do OnPoint (APO-021, APO-046 a
 * APO-049): a apolice, o endosso mais recente, o laudo de emissao e os boletos.
 *
 * O laudo (P-APO-2) so aparece com `apolices.laudo.view`, como no OnPoint, em que o
 * item e so do ADMIN; a apolice de antes do laudo mostra o item indisponivel, com o
 * motivo. Ao lado da apolice vai a versao do documento: "Assinada" ou "Aguardando
 * assinatura". O endereco e temporario e abre em aba nova: quem baixa continua na
 * lista. Depois do laudo, um aviso diz qual modelo o montou: o da empresa, publicado
 * em Templates, ou o neutro do sistema (P-MIN-3).
 */
export function PolicyDownloadsMenu({
  policy,
  onBillets,
  size = 'sm',
}: {
  policy: Policy;
  /** Abre o dialogo "Boletos". */
  onBillets: () => void;
  size?: 'sm' | 'md';
}) {
  const toast = useToast();
  const { can } = useAuth();
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const menuId = `${useId()}-arquivos`;
  const assinatura = rotuloDaAssinatura(policy.document_signature_status);

  useEffect(() => {
    if (!aberto) return;
    const fora = (event: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(event.target as Node)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const baixar = useMutation({
    mutationFn: (qual: 'apolice' | 'endosso' | 'laudo') =>
      qual === 'apolice'
        ? policies.document(policy.id)
        : qual === 'laudo'
          ? policies.issuanceReport(policy.id)
          : policies.lastEndorsementDocument(policy.id),
    onSuccess: (link, qual) => {
      window.open(enderecoDeArquivo(link.url), '_blank', 'noopener,noreferrer');
      const aviso =
        qual === 'laudo' ? avisoDoModeloDoLaudo(link as IssuanceReportLink) : null;
      if (aviso) toast.info(aviso);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function escolher(acao: () => void) {
    setAberto(false);
    acao();
  }

  return (
    <div
      ref={caixa}
      className="relative inline-block"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setAberto(false);
      }}
    >
      {/* Na linha da tabela, o gatilho e o icone de baixar com o nome na dica; no
          cabecalho do detalhe, o botao escrito. */}
      {size === 'sm' ? (
        <AcaoComIcone
          acao="baixar"
          rotulo="Baixar arquivos"
          aria-haspopup="menu"
          aria-expanded={aberto}
          aria-controls={menuId}
          loading={baixar.isPending}
          onClick={() => setAberto((atual) => !atual)}
        />
      ) : (
        <Button
          size={size}
          variant="secondary"
          aria-expanded={aberto}
          aria-controls={menuId}
          loading={baixar.isPending}
          onClick={() => setAberto((atual) => !atual)}
        >
          Baixar arquivos
        </Button>
      )}
      {aberto ? (
        <div
          id={menuId}
          role="menu"
          aria-label="Baixar arquivos"
          className="absolute right-0 z-30 mt-1 flex w-72 flex-col rounded-md border border-line bg-surface py-1 shadow-lg"
        >
          {/* O documento gerado do modelo vinculado (P-MIN-2) ou o arquivo ligado. */}
          <ItemDoMenu
            rotulo="Download da apolice (original)"
            selo={temDocumentoDaApolice(policy) ? assinatura : null}
            aviso={motivoSemDocumento(policy)}
            onClick={() => escolher(() => baixar.mutate('apolice'))}
          />
          {(policy.endorsement_count ?? 0) > 0 ? (
            <ItemDoMenu
              rotulo="Download do endosso (mais recente)"
              onClick={() => escolher(() => baixar.mutate('endosso'))}
            />
          ) : null}
          {can(PERMISSAO_DO_LAUDO) ? (
            <ItemDoMenu
              rotulo="Download do laudo de emissao (original)"
              aviso={motivoSemLaudo(policy)}
              onClick={() => escolher(() => baixar.mutate('laudo'))}
            />
          ) : null}
          <ItemDoMenu
            rotulo="Download do(s) boleto(s) (original)"
            acao="boleto"
            onClick={() => escolher(onBillets)}
          />
        </div>
      ) : null}
    </div>
  );
}

function ItemDoMenu({
  rotulo,
  aviso,
  selo,
  acao = 'baixar',
  onClick,
}: {
  rotulo: string;
  aviso?: string | undefined;
  /** Rotulo curto ao lado do item: a versao do documento da apolice (P-APO-2). */
  selo?: string | null | undefined;
  /** O icone ao lado do texto: a seta de baixar, ou o recibo dos boletos. */
  acao?: 'baixar' | 'boleto' | undefined;
  onClick: () => void;
}) {
  const visual = tiposDeAcao[acao];
  return (
    <button
      type="button"
      role="menuitem"
      disabled={aviso !== undefined}
      title={aviso}
      onClick={onClick}
      className={cn(
        'flex items-start gap-2.5 px-3 py-2 text-left text-sm text-ink-700 hover:bg-ink-50',
        'disabled:cursor-not-allowed disabled:text-ink-400 disabled:hover:bg-transparent',
      )}
    >
      <Icon
        name={visual.icone}
        size={16}
        className={cn('mt-0.5 shrink-0', aviso ? 'text-ink-300' : iconTone[visual.tom])}
      />
      <span className="min-w-0">
        {rotulo}
        {selo ? (
          <span className="ml-2 rounded bg-ink-100 px-1.5 py-0.5 text-2xs font-medium text-ink-600">
            {selo}
          </span>
        ) : null}
        {aviso ? <span className="block text-2xs text-ink-400">{aviso}</span> : null}
      </span>
    </button>
  );
}
